import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { X } from 'lucide-react';
import { DatePicker } from '@/components/onboarding/DatePicker';
import { useRelationshipTypes, usePartialUpdateContact } from '@/hooks/api/useContacts';
import type { ContactCreateUpdateRequest, RelationshipTypeEnum } from '@/types/api';
import { format } from 'date-fns';
import { ContactPreviewCard } from '@/components/contacts/ContactPreviewCard';
import { AvatarSelectorForm } from '@/components/contacts/AvatarSelectorForm';

type AddMethod = 'google' | 'manual' | '';

interface AddContactSectionProps {
  onClose: () => void;
  onGoogleImport: () => void;
  onManualSave: (contactData: ContactCreateUpdateRequest) => void;
  onFormStateChange?: (state: {
    contactName: string;
    phone: string;
    relationshipType: string;
    birthday?: Date;
    relationshipData: Record<string, any>;
    selectedAvatar: string;
  }) => void;
  skipSelection?: boolean; // If true, skip the selection screen and go directly to form
}

const AVAILABLE_AVATARS = ['m1.png', 'm2.png', 'm3.png', 'f1.png', 'f2.png', 'f3.png'];

function getRandomAvatar() {
  return AVAILABLE_AVATARS[Math.floor(Math.random() * AVAILABLE_AVATARS.length)];
}

export function AddContactSection({ onClose, onGoogleImport, onManualSave, onFormStateChange, skipSelection = false }: AddContactSectionProps) {
  const [step, setStep] = useState<'select' | 'form'>(skipSelection ? 'form' : 'select');
  const [method, setMethod] = useState<AddMethod>(skipSelection ? 'manual' : '');
  const [contactName, setContactName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [relationshipType, setRelationshipType] = useState<RelationshipTypeEnum | ''>('');
  const [birthday, setBirthday] = useState<Date | undefined>(undefined);
  const [relationshipData, setRelationshipData] = useState<Record<string, any>>({});
  const [error, setError] = useState('');
  const [selectedAvatar, setSelectedAvatar] = useState(getRandomAvatar());
  const [createdContactId, setCreatedContactId] = useState<number | null>(null);

  const { data: relationshipTypes, isLoading: isLoadingTypes } = useRelationshipTypes();
  const updateContactAvatar = usePartialUpdateContact(createdContactId || 0);

  // Notify parent of form state changes
  useEffect(() => {
    if (onFormStateChange) {
      onFormStateChange({
        contactName,
        phone,
        relationshipType,
        birthday,
        relationshipData,
        selectedAvatar,
      });
    }
  }, [contactName, phone, relationshipType, birthday, relationshipData, selectedAvatar, onFormStateChange]);

  const handleNext = () => {
    if (method === 'google') {
      onGoogleImport();
    } else if (method === 'manual') {
      setStep('form');
    }
  };

  const getDynamicFields = () => {
    if (!relationshipType || !relationshipTypes) return [];
    const typeData = relationshipTypes.find((t: any) => t.type === relationshipType);
    return typeData?.fields || [];
  };

  const formatLabel = (fieldName: string) => {
    return fieldName
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const handleManualSave = async () => {
    const trimmedName = contactName.trim();

    if (!trimmedName) {
      setError('Please enter a contact name');
      return;
    }

    if (trimmedName.length < 2) {
      setError('Name must be at least 2 characters');
      return;
    }

    setError('');

    const contactData: ContactCreateUpdateRequest = {
      name: trimmedName,
      email: email.trim() || undefined,
      phone: phone.trim() || undefined,
      avatar: selectedAvatar,
    };

    if (relationshipType) {
      contactData.relationship_type = relationshipType;
    }

    if (birthday) {
      contactData.birthday = format(birthday, 'yyyy-MM-dd');
    }

    // Add relationship data (dynamic fields including favorite_things)
    if (Object.keys(relationshipData).length > 0) {
      contactData.relationship_data = relationshipData;
    }

    onManualSave(contactData);
  };

  return (
    <div className="bg-white rounded-lg p-6">
      <div className="mb-6">
        <h3 className="text-[20px] font-bold font-euclid text-wabisabi-plum">Add Contact</h3>
      </div>

      {step === 'select' && (
        <>
          <p className="text-gray-600 mb-6">Choose how you'd like to add contacts</p>

          <div className="mb-6">
            <RadioGroup
              value={method}
              onValueChange={(value) => setMethod(value as AddMethod)}
              className="space-y-3"
            >
              <Label
                htmlFor="google"
                className="flex items-center space-x-3 border border-gray-200 rounded-md p-4 hover:bg-gray-50 cursor-pointer"
              >
                <RadioGroupItem value="google" id="google" />
                <span className="text-[14px] md:text-base font-normal text-gray-700 flex-1">
                  Google Contacts
                </span>
              </Label>

              <Label
                htmlFor="manual"
                className="flex items-center space-x-3 border border-gray-200 rounded-md p-4 hover:bg-gray-50 cursor-pointer"
              >
                <RadioGroupItem value="manual" id="manual" />
                <span className="text-[14px] md:text-base font-normal text-gray-700 flex-1">
                  Add contacts manually
                </span>
              </Label>
            </RadioGroup>
          </div>

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={onClose}
              className="border-gray-200 text-gray-600"
            >
              Cancel
            </Button>
            <Button
              onClick={handleNext}
              disabled={!method}
              className="bg-[#6C4F77] hover:bg-[#5A3A68] disabled:bg-gray-300 text-white"
            >
              Next
            </Button>
          </div>
        </>
      )}

      {step === 'form' && method === 'manual' && (
        <>
          <div className="space-y-4">
              {/* Avatar Selector */}
              <AvatarSelectorForm
                selectedAvatar={selectedAvatar}
                onAvatarSelect={setSelectedAvatar}
              />

              {/* Contact Name */}
              <div className="space-y-2">
              <Label htmlFor="contact-name" className="text-[#666666] text-sm">
                What's their full name *
              </Label>
              <Input
                id="contact-name"
                type="text"
                placeholder="Ex. John Smith"
                value={contactName}
                onChange={(e) => {
                  setContactName(e.target.value);
                  setError('');
                }}
                className={`h-12 border-gray-200 focus:border-[#6C4F77] focus:ring-[#6C4F77] ${
                  error ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                }`}
                autoFocus
              />
              {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="contact-phone" className="text-[#666666] text-sm">
                What's their phone number
              </Label>
              <Input
                id="contact-phone"
                type="tel"
                placeholder="Ex. +1 (555) 123-4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="h-12 border-gray-200 focus:border-[#6C4F77] focus:ring-[#6C4F77]"
              />
            </div>

            {/* Relationship Type */}
            <div className="space-y-2">
              <Label htmlFor="relationship" className="text-[#666666] text-sm">
                Relationship type
              </Label>
              <Select
                value={relationshipType}
                onValueChange={(value) => {
                  setRelationshipType(value as RelationshipTypeEnum);
                  // Clear relationship data when type changes
                  setRelationshipData({});
                }}
                disabled={isLoadingTypes}
              >
                <SelectTrigger className="h-12 border-gray-200 focus:border-[#6C4F77] focus:ring-[#6C4F77]">
                  <SelectValue
                    placeholder={isLoadingTypes ? 'Loading...' : 'Select relationship type'}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mother">Mother</SelectItem>
                  <SelectItem value="father">Father</SelectItem>
                  <SelectItem value="sister">Sister</SelectItem>
                  <SelectItem value="brother">Brother</SelectItem>
                  <SelectItem value="spouse">Spouse</SelectItem>
                  <SelectItem value="child">Child</SelectItem>
                  <SelectItem value="friend">Friend</SelectItem>
                  <SelectItem value="colleague">Colleague</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Birthday */}
            <div className="space-y-2">
              <Label htmlFor="birthday" className="text-[#666666] text-sm">
                Birthday
              </Label>
              <DatePicker
                id="birthday"
                value={birthday}
                onChange={setBirthday}
                placeholder="Select date"
              />
            </div>

            {/* Dynamic fields based on relationship type */}
            {relationshipType && getDynamicFields().length > 0 && (
              <>
                {getDynamicFields().map((field: any) => {
                  const getInputType = () => {
                    switch (field.type) {
                      case 'CharField':
                      case 'TextField':
                        return 'text';
                      case 'DateField':
                        return 'date';
                      case 'BooleanField':
                        return 'checkbox';
                      default:
                        return 'text';
                    }
                  };

                  const inputType = getInputType();

                  if (inputType === 'checkbox') {
                    return (
                      <div key={field.name} className="flex items-center space-x-2">
                        <input
                          id={field.name}
                          type="checkbox"
                          checked={relationshipData[field.name] || false}
                          onChange={(e) =>
                            setRelationshipData({
                              ...relationshipData,
                              [field.name]: e.target.checked,
                            })
                          }
                          className="h-4 w-4 text-wabisabi-plum focus:ring-wabisabi-plum border-gray-300 rounded"
                        />
                        <Label htmlFor={field.name} className="text-[#666666] text-sm">
                          {formatLabel(field.name)}
                        </Label>
                      </div>
                    );
                  }

                  if (inputType === 'date') {
                    return (
                      <div key={field.name} className="space-y-2">
                        <Label htmlFor={field.name} className="text-[#666666] text-sm">
                          {formatLabel(field.name)}
                          {field.required && <span className="text-red-500 ml-1">*</span>}
                        </Label>
                        <DatePicker
                          id={field.name}
                          value={
                            relationshipData[field.name]
                              ? new Date(relationshipData[field.name])
                              : undefined
                          }
                          onChange={(date) => {
                            setRelationshipData({
                              ...relationshipData,
                              [field.name]: date ? format(date, 'yyyy-MM-dd') : '',
                            });
                          }}
                          placeholder={
                            field.help_text || `Select ${formatLabel(field.name).toLowerCase()}`
                          }
                        />
                      </div>
                    );
                  }

                  return (
                    <div key={field.name} className="space-y-2">
                      <Label htmlFor={field.name} className="text-[#666666] text-sm">
                        {formatLabel(field.name)}
                        {field.required && <span className="text-red-500 ml-1">*</span>}
                      </Label>
                      <Input
                        id={field.name}
                        type={inputType}
                        placeholder={
                          field.help_text || `Enter ${formatLabel(field.name).toLowerCase()}`
                        }
                        value={relationshipData[field.name] || ''}
                        onChange={(e) =>
                          setRelationshipData({
                            ...relationshipData,
                            [field.name]: e.target.value,
                          })
                        }
                        required={field.required}
                        className="h-12 border-gray-200 focus:border-[#6C4F77] focus:ring-[#6C4F77]"
                      />
                    </div>
                  );
                })}
              </>
            )}
          </div>

          <div className="flex justify-start gap-3 mt-6">
            <Button
              onClick={handleManualSave}
              className="bg-[#6C4F77] hover:bg-[#6C4F77]/90 text-white"
            >
              Save Contact
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
